import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState(""); // yyyy-mm-dd
  const [toDate, setToDate] = useState(""); // yyyy-mm-dd

  // sorting
  const [sortBy, setSortBy] = useState("date"); // date | total | status
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  const navigate = useNavigate();

  const getErrorMessage = (err, fallback = "Something went wrong.") =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(
          "https://api.vyapakprint.com/api/orders",
        );
        const list = Array.isArray(response?.data?.orders)
          ? response.data.orders
          : [];
        setOrders(list);
      } catch (err) {
        setError(getErrorMessage(err, "Error fetching orders."));
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleViewOrder = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const normalize = (v) =>
    String(v || "")
      .toLowerCase()
      .trim();

  const inDateRange = (orderDate) => {
    if (!orderDate) return true;
    const d = new Date(orderDate);

    if (fromDate) {
      const f = new Date(fromDate);
      f.setHours(0, 0, 0, 0);
      if (d < f) return false;
    }
    if (toDate) {
      const t = new Date(toDate);
      t.setHours(23, 59, 59, 999);
      if (d > t) return false;
    }
    return true;
  };

  const filteredSortedOrders = useMemo(() => {
    const q = normalize(search);

    let list = [...orders];

    // filter: status
    if (statusFilter !== "ALL") {
      list = list.filter(
        (o) => normalize(o?.status) === normalize(statusFilter),
      );
    }

    // filter: date range
    list = list.filter((o) => inDateRange(o?.orderDate));

    // filter: search
    if (q) {
      list = list.filter((o) => {
        const orderId = normalize(o?._id);
        const name = normalize(o?.userDetails?.name);
        const email = normalize(o?.userDetails?.email);
        const mobile = normalize(o?.userDetails?.mobileNo);
        const business = normalize(o?.userDetails?.businessName);

        return (
          orderId.includes(q) ||
          name.includes(q) ||
          email.includes(q) ||
          mobile.includes(q) ||
          business.includes(q)
        );
      });
    }

    // sort
    list.sort((a, b) => {
      let av, bv;

      if (sortBy === "date") {
        av = a?.orderDate ? new Date(a.orderDate).getTime() : 0;
        bv = b?.orderDate ? new Date(b.orderDate).getTime() : 0;
      } else if (sortBy === "total") {
        av = money(a?.totalPrice);
        bv = money(b?.totalPrice);
      } else if (sortBy === "status") {
        av = normalize(a?.status);
        bv = normalize(b?.status);
      } else {
        av = 0;
        bv = 0;
      }

      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [orders, search, statusFilter, fromDate, toDate, sortBy, sortDir]);

  const uniqueStatuses = useMemo(() => {
    const set = new Set(
      orders.map((o) => (o?.status ? String(o.status) : "")).filter(Boolean),
    );
    // keep common order first
    const common = ["Processing", "Shipped", "Delivered", "Cancelled"];
    const rest = [...set].filter((s) => !common.includes(s)).sort();
    return ["ALL", ...common.filter((s) => set.has(s)), ...rest];
  }, [orders]);

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Orders</h1>

        <div className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold">{filteredSortedOrders.length}</span> /{" "}
          <span className="font-semibold">{orders.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search: order id / name / email / mobile"
            className="border rounded-lg px-3 py-2 w-full"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 w-full"
          >
            {uniqueStatuses.map((s) => (
              <option key={s} value={s}>
                {s === "ALL" ? "All Status" : s}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border rounded-lg px-3 py-2 w-full"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border rounded-lg px-3 py-2 w-full"
          />
        </div>

        {/* Sorting */}
        <div className="flex flex-col md:flex-row gap-3 mt-3">
          <div className="flex gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="date">Sort by Date</option>
              <option value="total">Sort by Total</option>
              <option value="status">Sort by Status</option>
            </select>

            <button
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="border rounded-lg px-4 py-2 hover:bg-gray-50"
              title="Toggle sort direction"
            >
              {sortDir === "asc" ? "ASC ↑" : "DESC ↓"}
            </button>
          </div>

          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("ALL");
              setFromDate("");
              setToDate("");
              setSortBy("date");
              setSortDir("desc");
            }}
            className="md:ml-auto border rounded-lg px-4 py-2 hover:bg-gray-50"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="text-center py-6 text-gray-600">Loading orders...</div>
      )}
      {error && !loading && (
        <div className="text-center py-6 text-red-600">{error}</div>
      )}

      {/* Orders Table */}
      {!loading && !error && (
        <div className="overflow-x-auto bg-white border rounded-xl shadow-sm">
          <table className="min-w-full text-center">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="py-3 px-4">Sr No.</th>
                <th className="py-3 px-4">Order ID</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Customer</th>
                <th className="py-3 px-4">Total</th>
                <th className="py-3 px-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredSortedOrders.length > 0 ? (
                filteredSortedOrders.map((order, index) => (
                  <tr key={order._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{index + 1}</td>
                    <td className="py-3 px-4 font-mono text-xs md:text-sm break-all">
                      {order._id}
                    </td>
                    <td className="py-3 px-4">
                      {order.orderDate
                        ? new Date(order.orderDate).toISOString().split("T")[0]
                        : "N/A"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 border">
                        {order.status || "N/A"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {order.userDetails?.name || "Unknown"}
                    </td>
                    <td className="py-3 px-4 font-semibold">
                      ₹{money(order.totalPrice).toLocaleString("en-IN")}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleViewOrder(order._id)}
                        className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-6 text-gray-500">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Orders;
